/**
 * Customer Context
 * Global state management for customer profile data
 * Optimized for minimal API calls and efficient data loading
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { customerService } from '../services/customerService';
import { useToast } from './ToastContext';
import { useAuth } from './AuthContext';

const CustomerContext = createContext(undefined);

export function CustomerProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const { showToast } = useToast();
  const { isAuthenticated } = useAuth();

  /**
   * Fetch customer profile from API
   * @param {boolean} force - Force refresh even if recently fetched
   */
  const fetchCustomer = useCallback(async (force = false) => {
    // Skip if not authenticated
    if (!isAuthenticated) {
      setCustomer(null);
      return;
    }

    // Skip if recently fetched (within 5 minutes) and not forced
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    if (!force && lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
      console.log('📦 Using cached customer data');
      return;
    }

    try {
      setIsLoading(true);
      const response = await customerService.getProfile();

      if (response.data.success) {
        setCustomer(response.data.data);
        setLastFetched(Date.now());
        console.log('✅ Customer data fetched:', response.data.data.fullName);
      }
    } catch (error) {
      console.error('❌ Failed to fetch customer data:', error);
      
      // Only show toast for non-401 errors (401 handled by api.js interceptor)
      if (error.response?.status !== 401) {
        showToast(
          error.response?.data?.message || 'Failed to load profile data',
          'error'
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, lastFetched, showToast]);

  /**
   * Update customer profile
   */
  const updateProfile = useCallback(async (data) => {
    try {
      const response = await customerService.updateProfile(data);

      if (response.data.success) {
        setCustomer(response.data.data);
        setLastFetched(Date.now());
        showToast('Profile updated successfully!', 'success');
        return { success: true, data: response.data.data };
      }
    } catch (error) {
      console.error('❌ Failed to update profile:', error);
      showToast(
        error.response?.data?.message || 'Failed to update profile',
        'error'
      );
      return { success: false, error };
    }
  }, [showToast]);

  /**
   * Add new address
   */
  const addAddress = useCallback(async (addressData) => {
    try {
      const response = await customerService.addAddress(addressData);

      if (response.data.success) {
        // Refresh customer data to get updated addresses
        await fetchCustomer(true);
        showToast('Address added successfully!', 'success');
        return { success: true, data: response.data.data };
      }
    } catch (error) {
      console.error('❌ Failed to add address:', error);
      showToast(
        error.response?.data?.message || 'Failed to add address',
        'error'
      );
      return { success: false, error };
    }
  }, [fetchCustomer, showToast]);

  /**
   * Update existing address
   */
  const updateAddress = useCallback(async (addressId, addressData) => {
    try {
      const response = await customerService.updateAddress(addressId, addressData);

      if (response.data.success) {
        // Update local state instead of full fetch for better performance
        setCustomer(prev => ({
          ...prev,
          addresses: prev.addresses.map(addr =>
            addr._id === addressId ? response.data.data.address : addr
          ),
        }));
        showToast('Address updated successfully!', 'success');
        return { success: true, data: response.data.data };
      }
    } catch (error) {
      console.error('❌ Failed to update address:', error);
      showToast(
        error.response?.data?.message || 'Failed to update address',
        'error'
      );
      return { success: false, error };
    }
  }, [showToast]);

  /**
   * Delete address
   */
  const deleteAddress = useCallback(async (addressId) => {
    try {
      const response = await customerService.deleteAddress(addressId);

      if (response.data.success) {
        // Refresh to get updated default address if needed
        await fetchCustomer(true);
        showToast('Address deleted successfully!', 'success');
        return { success: true };
      }
    } catch (error) {
      console.error('❌ Failed to delete address:', error);
      showToast(
        error.response?.data?.message || 'Failed to delete address',
        'error'
      );
      return { success: false, error };
    }
  }, [fetchCustomer, showToast]);

  /**
   * Deactivate account (soft delete)
   */
  const deactivateAccount = useCallback(async () => {
    try {
      const response = await customerService.deactivateAccount();

      if (response.data.success) {
        showToast('Account deactivated successfully', 'success');
        return { success: true };
      }
    } catch (error) {
      console.error('❌ Failed to deactivate account:', error);
      showToast(
        error.response?.data?.message || 'Failed to deactivate account',
        'error'
      );
      return { success: false, error };
    }
  }, [showToast]);

  /**
   * Auto-fetch when authenticated; clear state on logout
   */
  useEffect(() => {
    if (isAuthenticated && !customer) {
      fetchCustomer();
    } else if (!isAuthenticated) {
      setCustomer(null);
      setLastFetched(null);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    // State
    customer,
    isLoading,
    lastFetched,

    // Methods
    fetchCustomer,
    updateProfile,
    addAddress,
    updateAddress,
    deleteAddress,
    deactivateAccount,
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
}

/**
 * Hook to use customer context
 */
export function useCustomer() {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error('useCustomer must be used within CustomerProvider');
  }
  return context;
}

export default CustomerContext;
